import { useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { MENU_LIMITS, OWN_PAGE_LIMITS, READ_ACCEPT, READ_LIMITS, aiJobOpen, editsCatalog, draftOverflow, guessedCount, houseLinkLabel, houseSuggestions, keepableOf, pagesText, markGuessed, marksToCheck, planChanges, planImport, priceInText, readingSheets, skippedText, twinsIn } from "@speisekarte/core";
import type { AiJob, MenuOwnPages, ReadFile, ReadingHouse, Reading as ReadingData, SkippedPage } from "@speisekarte/core";
import { uploadOwnPage } from "../own-pages.ts";
import { aheadText, useAiJob } from "../ai-job.ts";
import { api, messageOf, store, useStore } from "../store.ts";
import { useKept } from "../kept.ts";
import { PlanDetails } from "./Import.tsx";

/** A photo from the phone made smaller before it goes out: the model reads 2000 px as well, and it fits the limit. */
async function smaller(file: File): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, READ_LIMITS.photoSide / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 1_000_000) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((done) => canvas.toBlob(done, "image/jpeg", 0.85));
    return blob ? new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.jpg`, { type: "image/jpeg" }) : file;
  } catch {
    // A kind the browser cannot draw (HEIC in Chrome): as it is; the server says what to do.
    return file;
  }
}

/**
 * The files of the last reading, in the order sent, while the app runs (S50): a PDF's pages without
 * dishes can be kept as own pages from them. Never on the server after the reading (S41, Datenschutz);
 * gone with a reload, and then nothing is offered.
 */
let sent: File[] = [];

/** What reading the PDFs may keep: per file the pages before and after its dishes. */
interface Keep {
  file: number;
  name: string;
  front: number[];
  back: number[];
}

const HOUSE_LABELS: Record<keyof ReadingHouse, string> = { address: "Anschrift", phone: "Telefon", email: "E-Mail", website: "Website", instagram: "Instagram", facebook: "Facebook" };

/** The pages to keep, cut out of their PDFs and uploaded as own pages (S50); their ids for the draft. */
async function keptPages(keeps: Keep[]): Promise<MenuOwnPages> {
  const { cutPages } = await import("../ownPagesPdf.ts");
  const out: MenuOwnPages = { front: [], back: [] };
  for (const k of keeps) {
    const bytes = new Uint8Array(await sent[k.file]!.arrayBuffer());
    const base = k.name.replace(/\.pdf$/i, "");
    for (const [side, pages, word] of [["front", k.front, "Vorspann"], ["back", k.back, "Nachsatz"]] as const) {
      if (!pages.length) continue;
      const cut = await cutPages(bytes, pages);
      const page = await uploadOwnPage(new Blob([cut as BlobPart], { type: "application/pdf" }), `${word} aus ${base}`.slice(0, OWN_PAGE_LIMITS.name), side);
      out[side].push(page.id);
    }
  }
  return out;
}

const mb = (bytes: number) => `${(bytes / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`;

/**
 * „Alte Karte einlesen“ (S41): the old menu as PDF, Word, photos or pasted text; the model reads it,
 * the plan shows what comes in, as for an Excel workbook; on request a draft in the old order.
 * The reading is a job in the queue (S45): the page may be left meanwhile, the result waits.
 */
export function Reading() {
  const s = useStore();
  const data = s.data!;
  const canEdit = editsCatalog(s.role);
  const [info, setInfo] = useState<{ available: boolean; left: number | null; demo: boolean } | null>(null);
  const [job, setJob] = useState<AiJob<"read"> | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [text, setText] = useKept("reading:text", "");
  const [read, setRead] = useKept<{ reading: ReadingData; from: string; files?: ReadFile[]; unread?: SkippedPage[] } | null>("reading", null);
  const [withMenu, setWithMenu] = useState(true);
  // What the card says about the house (S50): suggested for the settings, each taken unless unticked.
  const [leftHouse, setLeftHouse] = useState<(keyof ReadingHouse)[]>([]);
  // The pages of a PDF without dishes, to keep as own pages (S50): offered, never ticked beforehand.
  const [keeps, setKeeps] = useState<Keep[]>([]);
  const [keep, setKeep] = useState(false);
  const [state, setState] = useState<{ ok?: string; menu?: string; error?: string; busy?: "read" | "apply" }>({});

  useEffect(() => {
    let live = true;
    api<{ available: boolean; left: number | null; demo: boolean; job: AiJob<"read"> | null }>("/api/read")
      .then((r) => {
        if (!live) return;
        setInfo(r);
        if (r.job) setJob(r.job);
      })
      .catch(() => live && setInfo(null));
    return () => {
      live = false;
    };
  }, [read]);

  useAiJob(job, setJob, (done) => {
    if (done.status === "done" && done.result) {
      setRead({ reading: done.result.reading, from: done.result.from, files: done.result.files ?? [], unread: done.result.unread ?? [] });
      setLeftHouse([]);
      setState({});
    } else setState({ error: done.error?.message ?? "Das Einlesen ist abgebrochen. Bitte noch einmal." });
  });
  const reading = state.busy === "read" || (job !== null && aiJobOpen(job));

  const made = useMemo(() => (read ? readingSheets(read.reading, { allergens: data.allergens }) : null), [read, data.allergens]);
  const plan = useMemo(
    () => (made ? markGuessed(planImport(made.sheets, { dishes: data.dishes, allergens: data.allergens, categories: data.categories, drinkCategories: data.drinkCategories, drinkStart: data.settings.drinkStart, texts: data.texts }), made.guessed) : null),
    [made, data.dishes, data.allergens, data.categories, data.drinkCategories, data.settings.drinkStart, data.texts],
  );
  const size = files.reduce((n, f) => n + f.size, 0);

  async function choose(e: ChangeEvent<HTMLInputElement>) {
    const chosen = [...(e.target.files ?? [])];
    e.target.value = "";
    setState({});
    setFiles([...files, ...(await Promise.all(chosen.map(smaller)))].slice(0, READ_LIMITS.files));
  }

  async function start() {
    setState({ busy: "read" });
    const form = new FormData();
    for (const f of files) form.append("file", f);
    if (text.trim()) form.append("text", text.trim());
    try {
      const r = await api<{ job: AiJob<"read"> }>("/api/read", { form, timeout: 120_000 });
      setJob(r.job);
      sent = files;
      setFiles([]);
      setText("");
      setState({});
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  async function apply() {
    if (!read) return;
    setState({ busy: "apply" });
    try {
      const house = suggested.map((x) => x.field).filter((f) => !leftHouse.includes(f));
      // The pages to keep first: they come as own pages, the draft takes them before and after it.
      const ownPages = keep && keeps.length ? await keptPages(keeps) : null;
      const r = await store.change(() => api<{ result: { added: number; changed: number }; menu: { id: string; dropped: number } | null }>("/api/read/apply", { body: { reading: read.reading, menu: withMenu, house, ...(ownPages ? { ownPages } : {}) }, timeout: 60_000 }));
      setRead(null);
      setState({
        ok: `Übernommen: ${r.result.added} neu, ${r.result.changed} geändert.${r.menu?.dropped ? ` Für den Entwurf ${r.menu.dropped === 1 ? "ist die älteste gespeicherte Speisekarte" : `sind die ${r.menu.dropped} ältesten gespeicherten Speisekarten`} gelöscht.` : ""}`,
        ...(r.menu ? { menu: r.menu.id } : {}),
      });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  // Which PDFs of this reading are still at hand, and what of them could be kept (S50).
  useEffect(() => {
    let live = true;
    setKeeps([]);
    setKeep(false);
    const skippedNow = read?.reading.skipped ?? [];
    if (!read || !skippedNow.length || !canEdit) return;
    void (async () => {
      const { pageCount } = await import("../ownPagesPdf.ts");
      const out: Keep[] = [];
      for (const [k, f] of (read.files ?? []).entries()) {
        const held = sent[k];
        if (f.kind !== "pdf" || !held || held.name !== f.name) continue;
        const pages = await pageCount(new Uint8Array(await held.arrayBuffer()));
        if (!pages) continue;
        const { front, back } = keepableOf(skippedNow.filter((p) => p.file === k + 1).map((p) => p.page), pages);
        if (front.length || back.length) out.push({ file: k, name: f.name, front, back });
      }
      if (live) setKeeps(out);
    })();
    return () => {
      live = false;
    };
  }, [read, canEdit]);

  const guessed = plan ? guessedCount(plan) : 0;
  const suggested = read ? houseSuggestions({ address: "", phone: "", email: "", website: "", instagram: "", facebook: "", ...(read.reading.house as Partial<ReadingHouse> | undefined) }, data.settings) : [];
  const skipped = read?.reading.skipped ?? [];
  const unpriced = read ? priceInText(read.reading) : [];
  const twins = read ? twinsIn(read.reading) : [];
  const contradicting = read ? marksToCheck(read.reading) : [];
  return (
    <div className="import">
      <p>
        <a className="back" href="#/import">← Excel und Einlesen</a>
      </p>
      <section className="card">
        <h2>Alte Karte einlesen</h2>
        <p className="hint">Die bisherige Speisekarte als PDF, Word-Datei oder Fotos (eine Seite je Foto), oder ihr Text zum Hineinkopieren. Das Modell liest Speisen, Getränke, Preise und Allergene; vor dem Übernehmen siehst du, was dazukommt, wie bei einer Excel-Mappe.</p>
        {!canEdit ? (
          <p className="muted">Einlesen lassen nur Inhaber und Verwaltung.</p>
        ) : info && !info.available ? (
          <p className="muted">Auf diesem Server gibt es kein Einlesen.</p>
        ) : (
          <>
            <label className="upload">
              <span className="button">{files.length ? "Weitere Dateien …" : "Dateien wählen …"}</span>
              <input type="file" accept={READ_ACCEPT} multiple onChange={(e) => void choose(e)} hidden data-testid="read-files" />
            </label>
            {files.length > 0 && (
              <ul className="problems" data-testid="read-chosen">
                {files.map((f, i) => (
                  <li key={`${f.name}${i}`}>
                    {f.name} <span className="muted small">{mb(f.size)}</span>{" "}
                    <button className="link" onClick={() => setFiles(files.filter((_, k) => k !== i))} aria-label={`${f.name} entfernen`}>
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {size > READ_LIMITS.bytes && <p className="error-box">Zusammen {mb(size)}: höchstens {mb(READ_LIMITS.bytes)}. Bitte weniger Seiten oder Fotos.</p>}
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="… oder den Text der Karte hier einfügen" maxLength={READ_LIMITS.textChars} style={{ width: "100%", marginTop: "0.6rem" }} data-testid="read-text" />
            <div className="buttons">
              <button className="primary" disabled={(!files.length && !text.trim()) || size > READ_LIMITS.bytes || state.busy !== undefined || reading || info?.left === 0} onClick={() => void start()} data-testid="read-start">
                {reading ? "Liest die Karte …" : "Einlesen"}
              </button>
            </div>
            {reading && (
              <p className="hint" data-testid="read-waiting">
                {aheadText(job)} Das dauert meist eine halbe bis zwei Minuten. Du kannst die Seite verlassen: Das Ergebnis wartet hier.
              </p>
            )}
            {info?.left !== null && info?.left !== undefined && (
              <p className="muted small" data-testid="read-left">
                {info.left === 0 ? (info.demo ? "Im Demo-Zugang für heute alle Einlesungen verbraucht." : "Diesen Monat sind alle Einlesungen verbraucht.") : `Noch ${info.left === 1 ? "eine Einlesung" : `${info.left} Einlesungen`} ${info.demo ? "heute" : "diesen Monat"}.`}
              </p>
            )}
          </>
        )}
        {state.error && <p className="error-box">{state.error}</p>}
        {state.ok && (
          <p className="ok-text" data-testid="read-ok">
            {state.ok}{" "}
            {state.menu && (
              <a href={`#/speisekarte/${state.menu}`} data-testid="read-menu">
                Zum Entwurf der Speisekarte
              </a>
            )}
          </p>
        )}
      </section>

      {read && plan && made && (
        <section className="card" style={{ marginTop: "0.8rem" }} data-testid="read-plan">
          <h2>{read.reading.title || "Eingelesene Karte"}</h2>
          <p className="muted small">Gelesen aus: {read.from}. Bitte durchsehen, das Modell kann sich verlesen.</p>
          {skipped.length > 0 && (
            <p className="notice" data-testid="read-skipped">
              {skippedText(skipped, read.files ?? [])} {skipped.length === 1 ? "enthält" : "enthalten"} keine Speisen und {skipped.length === 1 ? "wird" : "werden"} nicht übernommen: {[...new Set(skipped.map((p) => p.what).filter(Boolean))].join(", ")}.
            </p>
          )}
          {read.unread?.length ? (
            <p className="warn" data-testid="read-unread">
              {skippedText(read.unread, read.files ?? [])}: nichts gelesen, auch beim zweiten Versuch nicht. Was dort steht, fehlt im Plan. Bitte diese Seiten noch einmal einlesen, etwa als Foto.
            </p>
          ) : null}
          {suggested.length > 0 && (
            <div data-testid="read-house">
              <p className="small" style={{ margin: "0.6rem 0 0.2rem" }}>
                Für „Name und Angaben“ unter „Konto“:
              </p>
              {suggested.map((x) => (
                <label key={x.field} className="check">
                  <input type="checkbox" checked={!leftHouse.includes(x.field)} onChange={(e) => setLeftHouse(e.target.checked ? leftHouse.filter((f) => f !== x.field) : [...leftHouse, x.field])} data-testid={`read-house-${x.field}`} />
                  <span>
                    {HOUSE_LABELS[x.field]}: {x.field === "address" || x.field === "phone" || x.field === "email" ? x.value : houseLinkLabel(x.field, x.value)}
                    {(x.field === "address" ? data.settings.address : data.settings[x.field]) ? <span className="muted small"> (statt „{x.field === "address" ? data.settings.address : x.field === "phone" || x.field === "email" ? data.settings[x.field] : houseLinkLabel(x.field, data.settings[x.field] ?? "")}“)</span> : null}
                  </span>
                </label>
              ))}
            </div>
          )}
          {guessed > 0 && (
            <p className="notice" data-testid="read-guessed">
              {read.reading.codesOnCard
                ? `Bei ${guessed === 1 ? "einer Speise" : `${guessed} Speisen und Getränken`} hat das Modell Merkmale wie „vegan“ gelesen`
                : `Die Karte nennt keine Allergene. Bei ${guessed === 1 ? "einer Speise" : `${guessed} Speisen und Getränken`} hat das Modell sie vermutet`}
              : Sie werden übernommen und bleiben als „Angaben ungeprüft“ markiert, bis du sie ansiehst. Verantwortlich für die Angaben bleibt der Betrieb.
            </p>
          )}
          {made.codes.length > 0 && (
            <details open data-testid="read-codes">
              <summary>Kürzel der alten Karte ({made.codes.length})</summary>
              <ul className="problems">
                {made.codes.map((c) => {
                  const to = data.allergens.find((a) => a.code === c.to);
                  return (
                    <li key={c.card}>
                      <b>{c.card}</b> {c.name} → {c.added ? <span>neu als „{c.card}“</span> : to ? <span><b>{to.code}</b> {to.name}</span> : <span className="error">bleibt weg</span>}
                    </li>
                  );
                })}
              </ul>
            </details>
          )}
          {made.unknownCodes.length > 0 && <p className="warn">Ohne Erklärung auf der Karte, bleiben weg: {made.unknownCodes.join(", ")}</p>}
          {unpriced.length > 0 && (
            <p className="warn" data-testid="read-unpriced">
              Ohne Preis, aber mit einem Betrag in der Beschreibung: {unpriced.join(", ")}. Bitte nach dem Übernehmen den Preis eintragen.
            </p>
          )}
          {twins.length > 0 && (
            <p className="warn" data-testid="read-twins">
              Auf der Karte gleich benannt, aber verschieden: {twins.join(", ")}. Bitte nach dem Übernehmen einen eigenen Namen geben.
            </p>
          )}
          {contradicting.length > 0 && (
            <p className="warn" data-testid="read-marks">
              Fleisch oder Fisch, aber vegan oder vegetarisch: {contradicting.join(", ")}. Bitte nach dem Übernehmen prüfen.
            </p>
          )}
          <PlanDetails plan={plan} />
          <label className="check" style={{ marginTop: "0.6rem" }}>
            <input type="checkbox" checked={withMenu} onChange={(e) => setWithMenu(e.target.checked)} data-testid="read-with-menu" />
            <span>Dazu eine Speisekarte im Aufbau der alten (Entwurf)</span>
          </label>
          {withMenu && draftOverflow(read.reading) > 0 && (
            <p className="muted small" data-testid="read-overflow">
              Eine Speisekarte hat höchstens {MENU_LIMITS.sections} Abschnitte: Die letzten {draftOverflow(read.reading)} kommen in den Katalog, aber nicht in den Entwurf.
            </p>
          )}
          {keeps.length > 0 && (data.ownPages?.length ?? 0) + keeps.reduce((n, k) => n + (k.front.length ? 1 : 0) + (k.back.length ? 1 : 0), 0) <= OWN_PAGE_LIMITS.perBusiness && (
            <label className="check" data-testid="read-keep">
              <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} data-testid="read-keep-pages" />
              <span>
                {keeps
                  .map((k) => [k.front.length ? `${pagesText(k.front)} als Vorspann` : "", k.back.length ? `${pagesText(k.back)} als Nachsatz` : ""].filter(Boolean).join(", ") + (keeps.length > 1 ? ` (${k.name})` : ""))
                  .join("; ")}{" "}
                behalten: unter „Eigene PDF-Seiten“, genau so wie im PDF{withMenu ? ", und vor und hinter dem Entwurf" : ""}
              </span>
            </label>
          )}
          <div className="buttons">
            <button className="primary" disabled={(!planChanges(plan) && !withMenu) || state.busy !== undefined} onClick={() => void apply()} data-testid="read-apply">
              {state.busy === "apply" ? "Übernehmen …" : "Übernehmen"}
            </button>
            <button className="link" onClick={() => setRead(null)}>
              Verwerfen
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
